import React from 'react';
import PropTypes from 'prop-types';
import Theme from "./Theme.js";
import SingleSelector from "./listSelectors/SingleSelector.js";
import MultiSelector from "./listSelectors/MultiSelector.js";
/**
 * The Item represents a clickable and/or selectable element in the List. An
 * Item that supplies an item prop is selectable and clickable. An Item that 
 * supplies an eventData prop without an item prop will be clickable but not
 * selectable. 
 */
import { jsx as _jsx } from "react/jsx-runtime";
class Header extends React.Component {
  static contextType = Theme.Context;
  onClick = evt => {
    // eslint-disable-next-line no-unused-vars
    const {
      onClick,
      item,
      eventData: data
    } = this.props;
    if (onClick && (item != null || data != null)) {
      onClick({
        item,
        data,
        sourceEvent: evt
      });
    }
  };
  render() {
    const theme = this.context;
    // eslint-disable-next-line no-unused-vars
    const {
      children,
      className,
      ...rest
    } = this.props;
    const icn = theme.subclasses('List', 'listHeader', className);
    return /*#__PURE__*/_jsx("div", {
      className: icn,
      onClick: this.onClick,
      ...rest,
      children: children
    });
  }
}

/**
 * The Item represents a clickable and/or selectable element in the List. An
 * Item that supplies an item prop is selectable and clickable. An Item that 
 * supplies an eventData prop without an item prop will be clickable but not
 * selectable. 
 */
class Item extends React.Component {
  static contextType = Theme.Context;
  onClick = evt => {
    // eslint-disable-next-line no-unused-vars
    const {
      onClick,
      item,
      eventData: data
    } = this.props;
    if (onClick && (item != null || data != null)) {
      onClick({
        item,
        data,
        sourceEvent: evt
      });
    }
  };
  render() {
    const theme = this.context;
    // eslint-disable-next-line no-unused-vars
    const {
      selected,
      children,
      className,
      contentClassName,
      disabled = false,
      onEvent,
      onClick,
      ...other
    } = this.props;
    const icn = theme.subclasses('List', 'listItem', className);
    const content = theme.subclasses('List', 'listItemContent', contentClassName);
    const rest = {
      ...other
    };
    if (disabled) {
      rest['data-disabled'] = true;
    }
    return /*#__PURE__*/_jsx("div", {
      className: icn,
      onClick: !disabled ? this.onClick : undefined,
      ...rest,
      "data-list-item-selected": selected,
      children: /*#__PURE__*/_jsx("div", {
        className: content,
        children: children
      })
    });
  }
}
let DYN = 1;
/**
 * This takes an items array and a factory and produces item elements for the list.
 * You can pass in component (a React element), a function ( that returns a react element
 * and takes item, props ) or a factory object with a create( item, props ) that also returns 
 * the react element. The react element should be a List.Item (if the item should be 
 * selectable/clickable) or other React component.  
 * The props will contain item, onClick, onEvent, selected, and any items given to itemProps.
 */
class Dynamic extends React.Component {
  static propTypes = {
    items: PropTypes.array.isRequired
  };
  static defaultProps = {
    className: null,
    variant: null,
    selected: null
  };
  constructor(props) {
    super(props);
    this.id = DYN++;
  }
  render() {
    const {
      items,
      component,
      creator,
      factory,
      keyFor,
      onClick,
      selector,
      onEvent,
      itemProps = {}
    } = this.props;
    if (items && (factory || creator || component)) {
      return /*#__PURE__*/_jsx(React.Fragment, {
        children: items.map((item, index) => {
          const key = typeof keyFor === 'function' ? keyFor(item) : typeof keyFor === 'string' ? item[keyFor] : `list-${this.id}-${index}`;
          const selected = selector != null && selector.isSelected(item);
          if (component) {
            const props = {
              ...itemProps,
              item,
              key,
              onClick,
              onEvent,
              selected
            };
            return /*#__PURE__*/React.createElement(component, props);
          }
          const props = {
            ...itemProps,
            key,
            onClick,
            onEvent,
            selected
          };
          if (creator) {
            return creator(item, props);
          }
          return factory.create(item, props);
        })
      });
    }
    return null;
  }
}

/** 
 * React class that renders a List element 
 * @property {string} className - css class to be applied to this component 
 * @property {string} variant - Theme variant name to be added to the component 
 * @property {function} onSelect - If a selection change occurs, this option function will
 * be called after the setState() has taken place 
 * @property {object} [selector] either null (no selection), SingleSelector, MultiSelector, or
 * a custom selector
 * @property {function} selector.onSelect called with an object containing
 * { value : <item>, data : <item's eventData>, listData : <List's eventData>, sourceEvent }.
 * This function should control the state of the selection.
 * @property {function} selector.isSelected returns true if the given item is selected
 *
 **/
class List extends React.Component {
  static contextType = Theme.Context;
  static propTypes = {
    className: PropTypes.string,
    variant: PropTypes.string
  };
  static defaultProps = {
    className: null,
    variant: null
  };
  onClick = evt => {
    const {
      selector,
      onClick,
      eventData = null,
      onSelect = null
    } = this.props;
    const {
      item,
      data,
      sourceEvent
    } = evt;
    if (onClick) {
      // The click event is not conveying a selection state change, so send this
      // like a button click (with just data filled out )
      onClick({
        data: item || data,
        itemData: item ? data || null : null,
        listData: eventData,
        sourceEvent
      });
    }
    if (selector && item) {
      selector.onSelect({
        value: item,
        data,
        listData: eventData,
        sourceEvent,
        onSelect
      });
    }
  };
  onEvent = evt => {
    const {
      onEvent
    } = this.props;
    if (onEvent) {
      onEvent(evt);
    }
  };
  render() {
    const theme = this.context;
    // eslint-disable-next-line no-unused-vars
    const {
      className,
      variant,
      children,
      onClick,
      onEvent,
      selector,
      layoutOptions,
      ...rest
    } = this.props;
    const mChildren = React.Children.map(children, (child, index) => {
      if (child == null || child.props == null) {
        return child;
      }
      const {
        item,
        eventData,
        onClick: itemOnClick
      } = child.props;
      const onClick = itemOnClick ? evt => {
        itemOnClick({
          ...evt,
          onClick: this.onClick
        });
      } : this.onClick;

      // If there is no identifying params or a click handler, the 
      // invoker is managing the click of this item. 
      let props;
      if (child.type === Dynamic) {
        props = {
          selector,
          onClick,
          onEvent: this.onEvent
        };
      } else if (item != null || eventData != null) {
        const selected = selector != null && selector.isSelected(item);
        props = {
          selected,
          onClick,
          onEvent: this.onEvent
        };
      } else {
        // This is no item, so this is not a item renderer, but extra deocators
        return child;
      }
      return /*#__PURE__*/React.cloneElement(child, props);
    });
    return /*#__PURE__*/_jsx("div", {
      className: theme.classes('List', 'list', variant, className),
      ...rest,
      children: mChildren
    });
  }
}
List.Item = Item;
List.Header = Header;
List.Dynamic = Dynamic;
List.SingleSelector = SingleSelector;
List.MultiSelector = MultiSelector;
export default List;